/* eslint-disable complexity */
import React, {useRef, useState, useEffect, useContext} from 'react';
import classnames from 'classnames';
import {isEqual} from 'lodash';
import PropTypes from 'prop-types';
import {Checkbox} from '@homeaway/react-form-components';
import {Circle} from '@homeaway/react-google-maps';
import {APP_NAMES, MARKER_TYPES} from '../../constants';
import Marker from '../Marker/Marker';
import SearchAreaButton from '../SearchAreaButton/SearchAreaButton';
import {
    getBoundsByMarkers,
    getMarkerUuid,
    iconSymbol,
    mapCentralPinSymbol,
} from '../../utils/markers';
import {CarouselIconDefinitions} from '../Carousel/Carousel';
import {
    LabelsPropType,
    MapApiConfigOptionsPropType,
    MarkerPropType,
    ThingsOfInterestPropType,
} from '../../utils/proptypes';
import {
    triggerEdapEventWithContext,
    GENERIC_EVENT,
    EDAP_EVENTS,
} from '../../utils/edapHelper';
import {TrackingContext} from '@homeaway/react-event-tracking';
import MapContext from '../../context/MapContext';
import DeferLoading from '../DeferLoading/DeferLoading';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import withDOM from '../withDOM/withDOM';

import './MapControl.less';

const addZoomListeners = (zoomControlDiv, map) => {
    if (map) {
        map.controls[5].push(zoomControlDiv);
        window?.google?.maps?.event?.addDomListener(
            zoomControlDiv.getElementsByClassName('Map__zoom-in')[0],
            'click',
            () => {
                map.setZoom(map.getZoom() + 1);
            }
        );
        window?.google?.maps?.event?.addDomListener(
            zoomControlDiv.getElementsByClassName('Map__zoom-out')[0],
            'click',
            () => {
                map.setZoom(map.getZoom() - 1);
            }
        );
    }
};

const handleCustomZoom = (customZoomControl, map) => {
    if (typeof customZoomControl === 'function') {
        const zoomControlDiv = customZoomControl();
        if (map && zoomControlDiv) {
            addZoomListeners(zoomControlDiv, map);
        }
    }
};

const MapControl = ({
    apiConfigOptions,
    bounds,
    customZoomControl,
    disableSearchAreaButton,
    disableSearchOnMove,
    enableSearchOnMove,
    isLoading,
    labels,
    onIdle,
    onMapLoaded,
    setMapMoveState,
    showAutoSearchCheckBox,
    shouldSearchOnMove,
    showMapSearchButton,
    showSearchAreaButton,
    thingsOfInterest,
    ...props
}) => {
    /**
     * this is used for triggering edap events
     * const {trigger} = useContext(TrackingContext);
     * Most likely, need to pass trigger to replace triggerEdapEvent function in react-toi
     */
    const googleRef = useRef(null);
    const userUpdatedMap = useRef(null);

    let resetMarkersTimer;
    const mapTimeout = 400;
    const [markers, setMarkers] = useState(props.markers);
    const context = useContext(TrackingContext);
    // eslint-disable-next-line no-undefined
    const [internalBounds, setInternalBounds] = useState(undefined);
    const [selectedMarker, setSelectedMarker] = useState(props.selectedMarker);
    const [hoverMarker, setHoverMarker] = useState(props.hoveredMarker);
    const [mapZoom, setMapZoom] = useState(props.zoom);

    const getToisBounds = (tois) => {
        let newBounds = new google.maps.LatLngBounds();
        const {lat, lng} = props;
        if (lat && lng) {
            newBounds.extend({lat, lng});
        }
        return tois.reduce((toiBounds, toi) => {
            return toiBounds.extend(toi.position);
        }, newBounds);
    };

    const fitToisInMap = () => {
        if (
            apiConfigOptions.appName === APP_NAMES.PDP &&
            googleRef &&
            googleRef.current &&
            googleRef.current.googleMap
        ) {
            const toiBounds = getToisBounds(thingsOfInterest);
            const map = googleRef.current.googleMap;
            map.fitBounds(toiBounds, props.fitBoundsPadding);
        }
    };

    useEffect(() => {
        if (googleRef && googleRef.current && googleRef.current.googleMap) {
            fitToisInMap();
        }
    }, [JSON.stringify(thingsOfInterest)]);

    useEffect(() => {
        setHoverMarker(props.hoveredMarker);
    }, [props.hoveredMarker]);

    useEffect(() => {
        setSelectedMarker(props.selectedMarker);
    }, [props.selectedMarker]);

    useEffect(() => {
        if (window.google) {
            if (props.markers && !bounds && !props.lat && !props.lng) {
                setInternalBounds(
                    getBoundsByMarkers(props.markers, window.google)
                );
            }
        }
    }, [window.google, bounds, props.lat, props.lng, props.markers]);

    useEffect(() => {
        setMarkers(props.markers);
    }, [props.markers]);

    const handleMapLoaded = (map) => {
        googleRef.current = {};
        googleRef.current.googleMap = map;

        handleCustomZoom(customZoomControl, map);

        triggerEdapEventWithContext(context, EDAP_EVENTS.MAP_PRESENTED, {
            action_location: apiConfigOptions.appName,
        });

        setTimeout(fitToisInMap, mapTimeout);

        if (typeof onMapLoaded === 'function') {
            onMapLoaded();
        }
    };

    const getMapState = (map) => {
        const {north, east, south, west} = map.getBounds().toJSON();
        const newBounds = {
            neLat: north,
            neLong: east,
            swLat: south,
            swLong: west,
        };

        const zoom = map.getZoom();
        return {bounds: newBounds, zoom};
    };

    const onMapMove = ({zoom: newZoom, bounds: newBounds}) => {
        const {updateMap} = props;
        /* Note: auto update happens here in this updateMap action */
        if (!isEqual(newBounds, bounds) || !isEqual(newZoom, mapZoom)) {
            if (typeof updateMap === 'function') {
                updateMap(newBounds, newZoom);
            }
        }
    };

    const handleIdle = () => {
        userUpdatedMap.current = false;
        if (typeof onIdle === 'function') {
            onIdle();
        }
    };

    const onMapUpdated = (map) => {
        onMapMove(getMapState(map));
    };

    const onIdleChanged = () => {
        if (shouldSearchOnMove) {
            clearTimeout(resetMarkersTimer);
            resetMarkersTimer = setTimeout(() => {
                handleIdle();
            }, mapTimeout);
        } else {
            handleIdle();
        }
    };

    const handleSerpZoomEvent = (newZoom) => {
        if (mapZoom !== newZoom) {
            const zoomtype = mapZoom > newZoom ? 'out' : 'in';
            triggerEdapEventWithContext(context, 'search.map.zoom', {
                zoomtype,
            });
        }
    };

    const onZoomChanged = () => {
        userUpdatedMap.current = true;
        if (googleRef && googleRef.current && googleRef.current.googleMap) {
            const map = googleRef.current.googleMap;
            const newZoom = map.getZoom();
            if (apiConfigOptions.appName === APP_NAMES.SERP) {
                handleSerpZoomEvent(newZoom);
            } else if (mapZoom && mapZoom !== newZoom) {
                if (newZoom > mapZoom) {
                    triggerEdapEventWithContext(
                        context,
                        EDAP_EVENTS.MAP_ZOOM_IN_SELECTED,
                        {
                            action_location: apiConfigOptions.appName,
                        }
                    );
                } else {
                    triggerEdapEventWithContext(
                        context,
                        EDAP_EVENTS.MAP_ZOOM_OUT_SELECTED,
                        {
                            action_location: apiConfigOptions.appName,
                        }
                    );
                }
            }
            if (typeof props.onZoomChanged === 'function') {
                props.onZoomChanged();
            }
            setMapZoom(newZoom);
        }
    };

    const onDragStart = () => {
        clearTimeout(resetMarkersTimer);
        if (typeof showMapSearchButton === 'function') {
            showMapSearchButton();
        }
        if (typeof setMapMoveState === 'function') {
            setMapMoveState(true);
        }

        userUpdatedMap.current = true;
        if (typeof props.onDragStart === 'function') {
            props.onDragStart();
        }
    };

    const onDragEnd = () => {
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            triggerEdapEventWithContext(context, 'search.map.move', {
                highfrequency: 'true',
            });
        }
        triggerEdapEventWithContext(context, EDAP_EVENTS.MAP_MOVE_SELECTED, {
            action_location: apiConfigOptions.appName,
        });
        if (typeof setMapMoveState === 'function') {
            setMapMoveState(false);
        }
        userUpdatedMap.current = true;
        if (typeof props.onDragEnd === 'function') {
            props.onDragEnd();
        }
    };

    const handleSerpSearchEvent = () => {
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            triggerEdapEventWithContext(context, GENERIC_EVENT, {
                eventcategory: 'search',
                eventaction: 'map search button click',
            });
        }
    };

    const handleSearch = () => {
        const {mapSearch, resetSearchButton, syncBounds} = props;

        handleSerpSearchEvent();

        if (typeof resetSearchButton === 'function') {
            resetSearchButton();
        }
        if (bounds) {
            if (typeof mapSearch === 'function') {
                mapSearch({
                    ...bounds,
                    zoom: mapZoom,
                });
            }
            if (typeof syncBounds === 'function') {
                syncBounds(bounds);
            }
        }
    };

    const handleSerpAutoSearchEvent = (event) => {
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            triggerEdapEventWithContext(context, GENERIC_EVENT, {
                eventaction: 'auto search checkbox',
                eventlabel: event.currentTarget.checked
                    ? 'checked'
                    : 'unchecked',
            });
        }
    };

    const handleMapSearch = () => {
        const {mapSearch} = props;
        if (typeof mapSearch === 'function') {
            mapSearch({
                ...bounds,
                zoom: mapZoom,
            });
        }
    };

    const handleAutoSearchCheck = (event) => {
        if (!event || !event.currentTarget) {
            return;
        }
        if (event.currentTarget.checked) {
            if (typeof enableSearchOnMove === 'function') {
                enableSearchOnMove();
            }
            handleMapSearch();
        } else if (typeof disableSearchOnMove === 'function') {
            disableSearchOnMove();
        }
        handleSerpAutoSearchEvent(event);
    };

    const onMapClick = () => {
        setSelectedMarker(null);
        if (typeof props.onToiClick === 'function') {
            props.onToiClick(null);
        }
    };

    const onMarkerClick = (uuid) => {
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            const newMarkers = [...markers];
            setMarkers(
                newMarkers.map((marker) => {
                    const markerUuid = getMarkerUuid(marker);
                    if (markerUuid === uuid) {
                        marker.isVisited = true;
                    }
                    return marker;
                })
            );
        }
        setSelectedMarker(uuid);
        if (typeof props.onMarkerClick === 'function') {
            props.onMarkerClick(uuid);
        }
        if (typeof props.onToiClick === 'function') {
            props.onToiClick(uuid);
        }
    };

    const onMarkerHover = (uuid) => {
        if (apiConfigOptions.appName !== APP_NAMES.SERP) {
            setHoverMarker(uuid);
        }
        if (typeof props.onMarkerHover === 'function') {
            props.onMarkerHover(uuid);
        }
    };

    const onMarkerHoverLeave = () => {
        if (apiConfigOptions.appName !== APP_NAMES.SERP) {
            setHoverMarker(null);
        }
        if (typeof props.onMarkerHoverLeave === 'function') {
            props.onMarkerHoverLeave();
        }
    };

    const renderMarkers = () => {
        const {siteName, appName} = apiConfigOptions;
        return markers.map((marker, index) => {
            const uuid = getMarkerUuid(marker);
            marker.isHighlighted = hoverMarker === uuid;
            marker.hoveredMarker = hoverMarker;
            if (appName === APP_NAMES.LANDINGAPP) {
                if (marker.isHighlighted) {
                    marker.zIndex = 11;
                } else {
                    marker.zIndex = markers.length - (index + 1);
                }
            }
            if (appName === APP_NAMES.SERP) {
                if (marker.isHighlighted) {
                    marker.zIndex = 999;
                } else {
                    marker.zIndex = markers.length - (index + 1);
                }
            }
            marker.isSelected = selectedMarker === uuid;
            marker.icon = iconSymbol(
                siteName,
                marker.isHighlighted,
                marker.isVisited || marker.isSelected,
                appName
            );
            if (marker.id === 'central-marker') {
                marker.icon = mapCentralPinSymbol();
            }
            return (
                <Marker
                    appName={appName}
                    key={index}
                    index={index}
                    onClick={onMarkerClick}
                    onHover={onMarkerHover}
                    onHoverLeave={onMarkerHoverLeave}
                    marker={marker}
                />
            );
        });
    };
    const renderCircle = () => {
        const {exact, lat, lng, optionsCircle} = props;
        return (
            !exact &&
            lat &&
            lng && <Circle id="circle" center={{lat, lng}} {...optionsCircle} />
        );
    };

    const renderToiMarkers = () => {
        if (thingsOfInterest) {
            return thingsOfInterest.map((marker, index) => {
                const uuid = getMarkerUuid(marker);
                marker.isSelected = selectedMarker === uuid;
                marker.isHighlighted = hoverMarker === uuid;
                marker.hoveredMarker = hoverMarker;
                return (
                    <Marker
                        key={index}
                        index={index}
                        onClick={onMarkerClick}
                        onHover={onMarkerHover}
                        onHoverLeave={onMarkerHoverLeave}
                        marker={marker}
                        type={MARKER_TYPES.TOI}
                    />
                );
            });
        }
        return null;
    };

    const AsyncGoogleMap = () => null;
    return (
        <MapContext.Provider
            value={{
                appName: apiConfigOptions.appName,
                isMobile: props.isMobile,
                labels: props.labels,
            }}
        >
            <CarouselIconDefinitions />
            <DeferLoading
                importComponent={AsyncGoogleMap}
                importFunc={() => {
                    return import(
                        /* webpackChunkName: "AsyncGoogleMap" */ '../AsyncGoogleMap/AsyncGoogleMap'
                    );
                }}
            >
                <AsyncGoogleMap
                    {...props}
                    bounds={bounds ? bounds : internalBounds}
                    labels={labels}
                    ref={googleRef}
                    onIdle={onIdleChanged}
                    onZoomChanged={onZoomChanged}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    apiConfigOptions={apiConfigOptions}
                    onMapClick={onMapClick}
                    onMapLoaded={handleMapLoaded}
                    ignoreFitBoundsPadding={
                        apiConfigOptions.appName !== APP_NAMES.SERP
                    }
                    onMapUpdated={onMapUpdated}
                >
                    {renderToiMarkers()}
                    {renderMarkers()}
                    {renderCircle()}
                    {showAutoSearchCheckBox && (
                        <Checkbox
                            className={classnames(
                                'Map__control',
                                'Map__control--auto-search'
                            )}
                            data-wdio={'Map__autoSearch'}
                            key={'auto_search'}
                            name={'auto_search'}
                            checked={shouldSearchOnMove}
                            onChange={handleAutoSearchCheck}
                            label={labels.autosearch}
                            size={'sm'}
                            inline
                        />
                    )}
                    <SearchAreaButton
                        isVisible={showSearchAreaButton}
                        isDisabled={disableSearchAreaButton}
                        onClick={handleSearch}
                        isLoading={isLoading}
                    />
                </AsyncGoogleMap>
            </DeferLoading>
        </MapContext.Provider>
    );
};

MapControl.propTypes = {
    /** Required map api configuration */
    apiConfigOptions: MapApiConfigOptionsPropType.isRequired,
    /** Array for markers you want to generate */
    markers: PropTypes.arrayOf(MarkerPropType.isRequired),
    /** Show the Auto search checkbox */
    showAutoSearchCheckBox: PropTypes.bool,
    /** Labels for entire map component. Only required if you are using custom features */
    labels: LabelsPropType,
    /** Boolean to determine to initiate search on map move. Sets to true only if Auto Search checkbox is checked  */
    shouldSearchOnMove: PropTypes.bool,
    /** Shows the Search Area Button. Button is only shown when Auto Search checkbox is unticked. */
    showSearchAreaButton: PropTypes.bool,
    /** Disables the Search Area Button */
    disableSearchAreaButton: PropTypes.bool,
    /** Boolean that determines if the map is loading */
    isLoading: PropTypes.bool,
    /** Boolean that determines to call updateMap method or not */
    allowMapUpdate: PropTypes.bool,
    /** Function that sets new bounds and zoom*/
    updateMap: PropTypes.func,
    /** Function that sets the mapMoveState to true or false on mapDrag */
    setMapMoveState: PropTypes.func,
    /** Function that sets the booleans shouldShowSearchButton and disableSearchAreaButton   */
    showMapSearchButton: PropTypes.func,
    /** Function that sets the bound and new zoom values*/
    mapSearch: PropTypes.func,
    /** Function that reset search button*/
    resetSearchButton: PropTypes.func,
    /** Function to sync new bounds */
    syncBounds: PropTypes.func,
    /** Function that sets the boolean shouldSearchOnMove to true */
    enableSearchOnMove: PropTypes.func,
    /** Function that sets the boolean shouldSearchOnMove to false */
    disableSearchOnMove: PropTypes.func,
    /** Function used to create custom zoom control on the map */
    customZoomControl: PropTypes.func,
    /** Callback fired when Map is in idle state */
    onIdle: PropTypes.func,
    /** Callback fired when zoom factor has changed */
    onZoomChanged: PropTypes.func,
    /** Callback fired when user has started to drag the map */
    onDragStart: PropTypes.func,
    /** Callback fired when user has released left click to end drag of map */
    onDragEnd: PropTypes.func,
    /** Set a marker to be selected */
    selectedMarker: PropTypes.string,
    /** Set a marker to be hovered */
    hoveredMarker: PropTypes.string,
    /** Callback fired when a marker is clicked */
    onMarkerClick: PropTypes.func,
    /** Callbac fired when a marker is hovered */
    onMarkerHover: PropTypes.func,
    /** Callback fired when the mouse has left hovered marker */
    onMarkerHoverLeave: PropTypes.func,
    /** Things of interest array for PDP application. Undefined for everything else */
    thingsOfInterest: PropTypes.arrayOf(ThingsOfInterestPropType),
    /** This prop will be used for SERP map expansion */
    shouldMapBeExpanded: PropTypes.bool,
    /** Fired when the map has been loaded  */
    onMapLoaded: PropTypes.func,
};

MapControl.defaultProps = {
    markers: [],
    showAutoSearchCheckBox: false,
    disableSearchAreaButton: false,
    selectedMarker: null,
    hoveredMarker: null,
    shouldMapBeExpanded: false,
};

export default withDOM(determineIfMobile(MapControl));
